import { animate } from "framer-motion";
import React, { createContext, useContext, useEffect, useReducer, useRef, useState, type ReactNode } from "react";
import { EASE_CALM } from "../lib/motion";
import {
	EQuestionnaireEvent,
	EStepSelection,
	type IQuestionnaireState,
	type IQuestionnaireStepDef,
	type TCtaHrefKey,
	type TQuestionnaireEvent,
	type TQuestionnairePath,
	getCurrentStep,
	getProgress,
	getStepsForPath,
	initialQuestionnaireState,
	questionnaireReducer,
	questionnaireTurnKey,
} from "./questionnaire-flow";
import { questionnaireRevealScrollTop, resolveQuestionnaireScrollport } from "./questionnaire-scroll";

/** Wall-clock for robot → bubbles → options stagger (latest turn only). */
export const STEP_INTRO_MS = 2600;
/** Hold selected tile before appending the answer and advancing. */
export const SELECTION_SETTLE_MS = 450;

export type TStepIntroPhase = "pending" | "playing" | "complete";

export type TQuestionnaireCtaHrefs = {
	getFundingHref: string;
	getStartedHref: string;
	manageCashFlowHref: string;
};

export type TQuestionnaireFlowContextValue = {
	path: TQuestionnairePath | null;
	state: IQuestionnaireState;
	step: IQuestionnaireStepDef | null;
	steps: IQuestionnaireStepDef[];
	progress: number;
	stepKey: string;
	/** Step-enter stagger phase for the live questionnaire turn. */
	stepIntro: TStepIntroPhase;
	stepPanelRef: React.RefObject<HTMLDivElement | null>;
	isAdvancing: boolean;
	settlingSelection: { stepIndex: number; optionId: string } | null;
	ctaHrefs: TQuestionnaireCtaHrefs;
	dispatch: React.Dispatch<TQuestionnaireEvent>;
	advanceToNextStep: (event: TQuestionnaireEvent) => void;
	selectSingleOption: (optionId: string, stepIndex?: number) => void;
	changePath: (nextPath: TQuestionnairePath) => void;
	handleOpenChange: (nextOpen: boolean) => void;
	resolveCtaHref: (key: TCtaHrefKey | undefined) => string;
};

const QuestionnaireFlowContext = createContext<TQuestionnaireFlowContextValue | null>(null);

export function useQuestionnaireFlow(): TQuestionnaireFlowContextValue {
	const ctx = useContext(QuestionnaireFlowContext);
	if (!ctx) {
		throw new Error("useQuestionnaireFlow must be used within QuestionnaireFlow.Root");
	}
	return ctx;
}

function prefersReducedMotion(): boolean {
	return typeof window !== "undefined" && window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function resolveCtaHref(key: TCtaHrefKey | undefined, hrefs: TQuestionnaireCtaHrefs): string {
	switch (key) {
		case "getFunding":
			return hrefs.getFundingHref;
		case "manageCashFlow":
			return hrefs.manageCashFlowHref;
		default:
			return hrefs.getStartedHref;
	}
}

type TQuestionnaireFlowRootProps = {
	path: TQuestionnairePath | null;
	/** When false, the path is selected on the picker but the flow has not opened yet. */
	active?: boolean;
	onOpenChange: (open: boolean) => void;
	onChangePath: (path: TQuestionnairePath) => void;
	ctaHrefs: TQuestionnaireCtaHrefs;
	children: ReactNode;
};

/**
 * Owns reducer + settle/intro timers. Compound parts consume via useQuestionnaireFlow.
 * Advances append to the transcript — prior turns are never swapped away.
 */
export function QuestionnaireFlowRoot({
	path,
	active = true,
	onOpenChange,
	onChangePath,
	ctaHrefs,
	children,
}: TQuestionnaireFlowRootProps) {
	const shouldBeOpen = Boolean(path) && active;
	const [state, dispatch] = useReducer(
		questionnaireReducer,
		{ path, shouldBeOpen },
		({ path: initialPath, shouldBeOpen: startOpen }) => {
			if (initialPath && startOpen) {
				return questionnaireReducer(initialQuestionnaireState, {
					type: EQuestionnaireEvent.OPEN,
					path: initialPath,
				});
			}
			return initialQuestionnaireState;
		},
	);
	const stepPanelRef = useRef<HTMLDivElement>(null);
	const advanceTimersRef = useRef<number[]>([]);
	const isAdvancingRef = useRef(false);
	const [isAdvancing, setIsAdvancing] = useState(false);
	const [settlingSelection, setSettlingSelection] = useState<{
		stepIndex: number;
		optionId: string;
	} | null>(null);
	const [stepIntro, setStepIntro] = useState<TStepIntroPhase>("pending");

	const step = getCurrentStep(state);
	const steps = getStepsForPath(state.path);
	const progress = getProgress(state);
	const stepKey = questionnaireTurnKey(state) || `${state.path ?? path}:${state.stepIndex}`;

	const clearAdvanceTimers = () => {
		for (const id of advanceTimersRef.current) {
			window.clearTimeout(id);
		}
		advanceTimersRef.current = [];
	};

	useEffect(() => {
		if (shouldBeOpen) {
			if (!path) return;
			if (state.path !== path || !state.open) {
				clearAdvanceTimers();
				/* Mount the opening turn hidden — the step-intro effect flips to "playing" on the
				 * next render and the stagger runs. Without this the turn mounts while stepIntro is
				 * still "complete" from the idle state and snaps visible with no animation. */
				setStepIntro("pending");
				dispatch({ type: EQuestionnaireEvent.OPEN, path });
			}
			return;
		}
		if (state.open) {
			clearAdvanceTimers();
			dispatch({ type: EQuestionnaireEvent.CLOSE });
		}
	}, [shouldBeOpen, path, state.path, state.open]);

	useEffect(() => {
		if (!state.path) {
			setStepIntro("complete");
			return;
		}
		isAdvancingRef.current = false;
		setIsAdvancing(false);
		setSettlingSelection(null);

		if (prefersReducedMotion()) {
			setStepIntro("complete");
		} else {
			// First paint of the new turn stays `pending` (opacity 0); history stays visible.
			setStepIntro("playing");
			const timer = window.setTimeout(() => {
				setStepIntro("complete");
			}, STEP_INTRO_MS);
			return () => window.clearTimeout(timer);
		}
	}, [stepKey, state.path]);

	/* One glide per turn, on the element that actually scrolls. The dialog panel is that
	 * element. The standalone page panel is overflow-visible, so the document (or a real
	 * ancestor scroller) has to move — writing scrollTop on the panel does nothing.
	 * Selection settle and multi toggles must not start a second glide. A transcript that
	 * still fits never scrolls. */
	useEffect(() => {
		const panel = stepPanelRef.current;
		if (!panel || !state.path) return;

		let cancelled = false;
		let controls: { stop: () => void } | null = null;
		const frame = window.requestAnimationFrame(() => {
			if (cancelled) return;
			const port = resolveQuestionnaireScrollport(panel);
			const from = port.scrollTop;
			const target = questionnaireRevealScrollTop(port, panel);
			const distance = target - from;
			if (distance <= 1) return;
			if (prefersReducedMotion()) {
				port.scrollTop = target;
				return;
			}
			controls = animate(0, 1, {
				duration: Math.min(1.4, Math.max(0.45, Math.abs(distance) / 600)),
				delay: 0.2,
				ease: EASE_CALM,
				onUpdate: (progress) => {
					const liveTarget = Math.max(from, questionnaireRevealScrollTop(port, panel));
					port.scrollTop = Math.max(port.scrollTop, from + (liveTarget - from) * progress);
				},
			});
		});

		return () => {
			cancelled = true;
			window.cancelAnimationFrame(frame);
			controls?.stop();
		};
	}, [stepKey, state.path]);

	useEffect(() => () => clearAdvanceTimers(), []);

	const advanceToNextStep = (event: TQuestionnaireEvent) => {
		if (isAdvancingRef.current) return;
		isAdvancingRef.current = true;
		setIsAdvancing(true);
		clearAdvanceTimers();
		setSettlingSelection(null);
		// Keep prior turns mounted; only the new turn intro-animates after stepIndex changes.
		setStepIntro("pending");
		dispatch(event);
	};

	const selectSingleOption = (optionId: string, stepIndex = state.stepIndex) => {
		if (isAdvancingRef.current) return;
		const target = steps[stepIndex];
		if (!target || target.selection !== EStepSelection.SINGLE) return;
		if (stepIndex < state.stepIndex && state.answers[target.id] === optionId) return;
		if (settlingSelection?.stepIndex === stepIndex && settlingSelection.optionId === optionId) return;
		setSettlingSelection({ stepIndex, optionId });
		clearAdvanceTimers();
		const settleMs = prefersReducedMotion() ? 0 : SELECTION_SETTLE_MS;
		const settleTimer = window.setTimeout(() => {
			advanceToNextStep({
				type: EQuestionnaireEvent.SELECT_OPTION,
				optionId,
				stepIndex,
			});
		}, settleMs);
		advanceTimersRef.current.push(settleTimer);
	};

	const handleOpenChange = (nextOpen: boolean) => {
		if (!nextOpen) {
			clearAdvanceTimers();
			dispatch({ type: EQuestionnaireEvent.CLOSE });
		}
		onOpenChange(nextOpen);
	};

	const changePath = (nextPath: TQuestionnairePath) => {
		if (nextPath === path) return;
		clearAdvanceTimers();
		onChangePath(nextPath);
	};

	const value: TQuestionnaireFlowContextValue = {
		path: state.path,
		state,
		step,
		steps,
		progress,
		stepKey,
		stepIntro,
		stepPanelRef,
		isAdvancing,
		settlingSelection,
		ctaHrefs,
		dispatch,
		advanceToNextStep,
		selectSingleOption,
		changePath,
		handleOpenChange,
		resolveCtaHref: (key) => resolveCtaHref(key, ctaHrefs),
	};

	return <QuestionnaireFlowContext.Provider value={value}>{children}</QuestionnaireFlowContext.Provider>;
}
